/**
 * Scale-bar arithmetic for the map panel, used in place of mapbox's own
 * ScaleControl. Nothing here imports mapbox-gl, so these run in the node test
 * project.
 *
 * ScaleControl measures the ground under a fixed run of pixels by unprojecting
 * both ends and calling `LngLat.distanceTo`, the spherical law of cosines:
 * `acos(sin.sin + cos.cos.cos(dLon))`. A few centimetres apart that cosine
 * sits within 1e-17 of 1, below what a double holds, so `acos` collapses to 0
 * and the measured distance is zero. Its rounding helper then computes
 * `10 ** ceil(-log(0))` -- Infinity -- multiplies the zero by it, and renders
 * the NaN. The panel fits centimetre-scale tracks to zooms where that happens.
 *
 * Web Mercator gives the same number in closed form with no cancellation, so
 * that is what this computes instead.
 */

/** Length of the equator, in metres: the width of the Mercator world. */
const EARTH_CIRCUMFERENCE_M = 40075016.686;

/** Width of the Mercator world at zoom 0, in pixels, for mapbox's 512px tiles. */
const WORLD_SIZE_PX_AT_ZOOM_0 = 512;

/**
 * Ground distance covered by one screen pixel. Mercator stretches by latitude
 * and halves with every zoom level, and that is the whole of it.
 */
export function metresPerPixel(latitudeDeg: number, zoom: number): number {
  return (
    (EARTH_CIRCUMFERENCE_M * Math.cos((latitudeDeg * Math.PI) / 180)) /
    (WORLD_SIZE_PX_AT_ZOOM_0 * 2 ** zoom)
  );
}

/**
 * The largest member of the 1-2-5 series -- 1, 2 or 5 times a power of ten --
 * at or below `metres`, so the bar lands on a distance a reader can divide by
 * eye. Expects a positive, finite `metres`.
 */
export function roundDownTo125Series(metres: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(metres));
  const leading = metres / magnitude;

  if (leading >= 5) {
    return 5 * magnitude;
  }

  if (leading >= 2) {
    return 2 * magnitude;
  }

  return magnitude;
}

/**
 * Units the bar may read in, largest first, as the metres each one measures.
 * A unit is chosen once the distance reaches one of it, so the same number is
 * both the threshold and the divisor.
 */
const UNITS: ReadonlyArray<readonly [metres: number, name: string]> = [
  [1000, "km"],
  [1, "m"],
  [0.01, "cm"],
];

/** What anything below a centimetre reads in. */
const MILLIMETRE: readonly [metres: number, name: string] = [0.001, "mm"];

/**
 * `metres` written in the largest unit that leaves it at or above 1, so a bar
 * spanning two centimetres says so rather than "0.02 m". Distances here are
 * always 1, 2 or 5 times a power of ten, so the conversion lands on a short
 * decimal; the rounding only guards against binary representation showing up
 * as a trail of nines.
 */
export function formatDistance(metres: number): string {
  let chosen = MILLIMETRE;
  for (const unit of UNITS) {
    if (metres >= unit[0]) {
      chosen = unit;
      break;
    }
  }

  const [metresPerUnit, name] = chosen;
  return `${Math.round((metres / metresPerUnit) * 100) / 100} ${name}`;
}
